// src/pages/EditArticle.tsx
import ArticleCompatibilitySection from '@/components/ArticleCompatibilitySection';
import Select from '@/components/common/Select.tsx';
import { supabase } from '@/lib/supabase';
import type { Category, Location, Subcategory } from '@/types';
import { logArticleCreated, logArticleDeleted, logArticleUpdated } from '@/utils/activityLogger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MapPin, Trash2 } from 'lucide-react';
import { useEffect, useState, type ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext";

interface ArticleFormData {
  codice_prodotto: string
  descrizione_prodotto: string
  categoria_id: string
  sottocategoria_id: string
  quantita_minima_magazzino: string
  quantita_magazzino_attuale: string
  costo_acquisto: string
  prezzo_vendita: string
  posizione_magazzino_id: string
  barcode: string
  note: string
}

type ArticleFormField = keyof ArticleFormData


export default function EditArticle() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const article = location.state?.article;
  const scanNotFound = location.state?.scanNotFound;
  const scannedBarcode = location.state?.scannedBarcode; // NEW: barcode from scan
  const isEditMode = !!article;

  const { profile } = useAuth();

  const [formData, setFormData] = useState<ArticleFormData>({
    codice_prodotto: '',
    descrizione_prodotto: '',
    categoria_id: '',
    sottocategoria_id: '',
    quantita_minima_magazzino: '0',
    quantita_magazzino_attuale: '0',
    costo_acquisto: '0',
    prezzo_vendita: '0',
    posizione_magazzino_id: '',
    barcode: '',
    note: ''
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load article data in edit mode
  useEffect(() => {
    if (article) {
      console.log('📝 [EDIT] Loading article data:', article);
      setFormData({
        codice_prodotto: article.codice_prodotto || '',
        descrizione_prodotto: article.descrizione_prodotto || '',
        categoria_id: article.categoria_id || '',
        sottocategoria_id: article.sottocategoria_id || '',
        quantita_minima_magazzino: String(article.quantita_minima_magazzino || 0),
        quantita_magazzino_attuale: String(article.quantita_magazzino_attuale || 0),
        costo_acquisto: String(article.costo_acquisto || 0),
        prezzo_vendita: String(article.prezzo_vendita || 0),
        posizione_magazzino_id: article.posizione_magazzino_id || '',
        barcode: article.barcode || '',
        note: article.note || ''
      });
    }
  }, [article]);

  // NEW: Handle scanned barcode for new articles
  useEffect(() => {
    if (scannedBarcode && !article) {
      console.log('📷 [SCAN] Pre-filling barcode for new article:', scannedBarcode);
      setFormData(prev => ({
        ...prev,
        codice_prodotto: scannedBarcode.toUpperCase(),
        barcode: scannedBarcode
      }));
    }
  }, [scannedBarcode, article]);

  // Handle return from LocationPicker - RESTORE FORM DATA + LOCATION
  useEffect(() => {
    // First restore form data if coming back
    if (location.state?.formData) {
      setFormData(location.state.formData);
    }

    // Then apply selected location on top
    if (location.state?.selectedLocation) {
      setFormData(prev => ({
        ...prev,
        posizione_magazzino_id: location.state.selectedLocation.id
      }));
    }
  }, [location.state?.formData, location.state?.selectedLocation]);

  // Fetch categories
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['categories', profile?.customer_uuid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('customer_uuid', profile!.customer_uuid)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.customer_uuid
  });

  // Fetch subcategories
  const { data: subcategories = [] } = useQuery<Subcategory[]>({
    queryKey: ['subcategories', profile?.customer_uuid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('customer_uuid', profile!.customer_uuid)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.customer_uuid
  });

  // Fetch locations
  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['locations', profile?.customer_uuid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('customer_uuid', profile!.customer_uuid)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.customer_uuid
  });

  // Get filtered subcategories
  const filteredSubcategories = subcategories.filter(
    sub => sub.category_id === formData.categoria_id
  );

  // Form change handler
  const handleFormChange = (field: ArticleFormField, value: string) => {
    if (field === 'codice_prodotto') {
      value = value.toUpperCase();
    }

    if (field === 'descrizione_prodotto') {
      value = value.toUpperCase();
    }

    if (['quantita_minima_magazzino', 'quantita_magazzino_attuale'].includes(field)) {
      value = value.replace(/[^0-9]/g, '');
    }

    if (['costo_acquisto', 'prezzo_vendita'].includes(field)) {
      // Accept both comma and dot as decimal separator
      value = value.replace(',', '.');
      value = value.replace(/[^0-9.]/g, '');
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'categoria_id') {
        newData.sottocategoria_id = '';
      }
      return newData;
    });
  };

  // Get full path for location
  const getFullPath = (locationId: string): string => {
    const location = locations.find(l => l.id === locationId);
    if (!location) return '';

    const parts: string[] = [];
    let current: Location | undefined = location;

    while (current) {
      parts.unshift(current.description);
      current = locations.find(l => l.id === current?.parent_id);
    }

    return parts.join(' > ');
  };

  // Create article
  const createArticle = useMutation<any, Error, Record<string, any>>({
    mutationFn: async (data) => {
      console.log('🔵 [INSERT] Starting article creation...');
      console.log('🔵 [INSERT] Input data:', data);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('🔴 [INSERT] Auth error:', authError);
        throw authError;
      }
      console.log('🔵 [INSERT] User ID:', user!.id);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('customer_uuid')
        .eq('id', user!.id)
        .single();

      if (profileError) {
        console.error('🔴 [INSERT] Profile error:', profileError);
        throw profileError;
      }
      console.log('🔵 [INSERT] Customer UUID:', profile.customer_uuid);

      const insertData = { ...data, customer_uuid: profile.customer_uuid };
      console.log('🔵 [INSERT] Full insert data:', insertData);

      const { data: result, error } = await supabase
        .from('articles')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('🔴 [INSERT] Supabase error:', error);
        console.error('🔴 [INSERT] Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ [INSERT] Success! Created article:', result);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      logArticleCreated(data.id, data.codice_prodotto);
      navigate('/articles');
    },
    onError: (error) => {
      console.error('🔴 [INSERT] Mutation error:', error);
      alert(`Errore durante la creazione: ${error.message}`);
    }
  });

  // Update article
  const updateArticle = useMutation<any, Error, Record<string, any> & { id: string }>({
    mutationFn: async ({ id, ...data }) => {
      const { error } = await supabase
        .from('articles')
        .update(data)
        .eq('id', id);

      if (error) throw error;
      return { id, codice_prodotto: data.codice_prodotto, ...data };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      logArticleUpdated(data.id, data.codice_prodotto);
      navigate('/articles');
    }
  });

  // Delete article
  const deleteArticle = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      logArticleDeleted(article.id, article.codice_prodotto);
      navigate('/articles');
    }
  });

  // Save handler
  const handleSave = () => {
    console.log('💾 [SAVE] Starting save...');
    console.log('💾 [SAVE] isEditMode:', isEditMode);
    console.log('💾 [SAVE] article:', article);

    if (!formData.codice_prodotto) {
      alert('Codice prodotto è obbligatorio');
      return;
    }

    const dataToSave = {
      codice_prodotto: formData.codice_prodotto,
      descrizione_prodotto: formData.descrizione_prodotto || '', // NOT NULL constraint - use empty string
      categoria_id: formData.categoria_id || null,
      sottocategoria_id: formData.sottocategoria_id || null,
      quantita_minima_magazzino: parseInt(formData.quantita_minima_magazzino) || 0,
      quantita_magazzino_attuale: parseInt(formData.quantita_magazzino_attuale) || 0,
      costo_acquisto: parseFloat(formData.costo_acquisto) || 0,
      prezzo_vendita: parseFloat(formData.prezzo_vendita) || 0,
      posizione_magazzino_id: formData.posizione_magazzino_id || null,
      barcode: formData.barcode || '',  // NOT NULL constraint - use empty string
      note: formData.note || ''  // NOT NULL constraint - use empty string
    };

    console.log('💾 [SAVE] Data to save:', dataToSave);

    if (isEditMode) {
      console.log('💾 [SAVE] Calling UPDATE with ID:', article.id);
      updateArticle.mutate({ id: article.id, ...dataToSave });
    } else {
      console.log('💾 [SAVE] Calling INSERT');
      createArticle.mutate(dataToSave);
    }
  };

  const isSaving = createArticle.isPending || updateArticle.isPending;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/articles')}
            className="p-2 hover:bg-slate-100 rounded transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-slate-800">
            {isEditMode ? 'Modifica Articolo' : 'Nuovo Articolo'}
          </h1>
          <div className="w-10"></div>
        </div>
        {scanNotFound && !isEditMode && (
          <div className="mx-4 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <p className="font-medium">Articolo non trovato</p>
            <p>Completa i dati per aggiungere questo articolo</p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Codice Prodotto */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Codice Prodotto * (maiuscolo)
          </label>
          <input
            type="text"
            value={formData.codice_prodotto}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('codice_prodotto', e.target.value)}
            placeholder="es. ART-001"
            readOnly={isEditMode}
            className={`w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base uppercase ${isEditMode ? 'bg-slate-100 cursor-not-allowed' : ''
              }`}
          />
          {isEditMode && (
            <p className="text-xs text-slate-500 mt-1">Il codice prodotto non può essere modificato</p>
          )}
        </div>

        {/* Descrizione */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Descrizione Prodotto
          </label>
          <textarea
            value={formData.descrizione_prodotto}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFormChange('descrizione_prodotto', e.target.value)}
            placeholder="Descrizione dettagliata (opzionale)..."
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-base uppercase"
          />
        </div>

        {/* Categoria + Sottocategoria */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Categoria</label>
            <Select
              value={formData.categoria_id}
              onChange={(e) => handleFormChange('categoria_id', e.target.value)}
              options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
              placeholder="Seleziona categoria..."
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Sottocategoria</label>
            <Select
              value={formData.sottocategoria_id}
              onChange={(e) => handleFormChange('sottocategoria_id', e.target.value)}
              options={filteredSubcategories.map(sub => ({ value: sub.id, label: sub.name }))}
              placeholder="Seleziona sottocategoria..."
              disabled={!formData.categoria_id}
              emptyMessage="Seleziona prima una categoria"
              className="w-full"
            />
          </div>
        </div>

        {/* Quantità */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quantità Minima</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.quantita_minima_magazzino}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('quantita_minima_magazzino', e.target.value)}
              placeholder="0"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quantità Attuale</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.quantita_magazzino_attuale}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('quantita_magazzino_attuale', e.target.value)}
              placeholder="0"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>
        </div>

        {/* Prezzi */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Costo di Acquisto (€)</label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.costo_acquisto}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('costo_acquisto', e.target.value)}
              placeholder="0,00"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Prezzo di Vendita (€)</label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.prezzo_vendita}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('prezzo_vendita', e.target.value)}
              placeholder="0,00"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>
        </div>

        {/* Posizione */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Posizione Magazzino</label>
          <button
            type="button"
            onClick={() => {
              navigate('/location-picker', {
                state: {
                  locations,
                  currentLocationId: formData.posizione_magazzino_id,
                  returnPath: isEditMode ? '/articles/edit' : '/articles/add',
                  article,
                  scanNotFound,
                  formData  // PASS CURRENT FORM DATA
                }
              });
            }}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-slate-50 transition-colors touch-manipulation flex items-center justify-between"
          >
            <span className={formData.posizione_magazzino_id ? 'text-slate-800' : 'text-slate-400'}>
              {formData.posizione_magazzino_id
                ? getFullPath(formData.posizione_magazzino_id)
                : 'Seleziona posizione...'}
            </span>
            <MapPin className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Barcode */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Barcode</label>
          <input
            type="text"
            value={formData.barcode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('barcode', e.target.value)}
            placeholder="Codice a barre..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
          />
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Note</label>
          <textarea
            value={formData.note}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFormChange('note', e.target.value)}
            placeholder="Note aggiuntive..."
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-base"
          />
        </div>

        {/* Compatibilità (only in edit mode) */}
        {isEditMode && profile?.customer_uuid && (
          <ArticleCompatibilitySection
            articleId={article.id}
            articleCode={article.codice_prodotto}
            customerUuid={profile.customer_uuid}
          />
        )}
      </div>

      {/* Footer Buttons */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-2xl mx-auto flex gap-3">
          {isEditMode && profile?.role !== 'operator' && profile?.role !== 'viewer' && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors touch-manipulation flex items-center gap-2"
            >
              <Trash2 className="w-5 h-5" />
              Elimina
            </button>
          )}
          <button
            onClick={() => navigate('/articles')}
            className="flex-1 px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors touch-manipulation"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors touch-manipulation"
          >
            {isSaving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Conferma Eliminazione</h3>
            <p className="text-slate-600 mb-6">
              Sei sicuro di voler eliminare l'articolo <span className="font-semibold">"{article.codice_prodotto}"</span>?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteArticle.mutate(article.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}